// Pale Blue Dot — kinetic typography sequence woven into the main site.
// Triggered by the "Pale Blue Dot — Carl Sagan" button on the landing.
// Fades out the CTA panel, layers a word-by-word transcript over the
// starbox, and slowly pulls the camera back from Earth. Earth shrinks on
// an artistic curve so it becomes the iconic "pale blue dot" by the
// 2-minute mark, and the rest of the speech rides out at the Voyager
// vantage with a sunbeam composition.

const TRANSCRIPT_URL = '/pale-blue-dot/transcript.json'

// Audio playback starts at "Consider again that dot" (~85.8s in the
// original recording). We open on a full Earth and the journey ends at
// "suspended in a sunbeam" (~145s), where the iconic Voyager photograph
// is fully reimagined and the camera holds for the rest of the speech.
export const SEQUENCE_START_TIME = 85.8

// Camera path. From sequence start until BLEND_END_T the camera blends
// FROM whatever intro position the user was on TO a centered view at
// (0, 0, 5.5) — pulling back AND drifting toward Earth-centered framing
// at the same time. After BLEND_END_T the camera is centered and just
// pulls straight back along the z-axis through CAM_KEYFRAMES.
const BLEND_END_T = 110

// The camera moves continuously through the entire ~3-minute audio.
// Slow opening (~70s of barely-moving drift while Earth lingers), then
// a graceful build, then the final approach to the iconic Voyager-PBD
// distance. Smoothstep within each segment keeps motion from pausing.
const CAM_KEYFRAMES = [
  // [absolute_audio_time, z_distance]
  [BLEND_END_T, 5.5],   // centered, still close — Earth dominant
  [150,         12.0],  // gentle drift
  [195,         40.0],  // building speed
  [240,         140.0], // most of the journey behind us
  [275,         219.4], // Voyager-PBD distance — iconic vantage at the end
]

function cameraZAt(t) {
  const keys = CAM_KEYFRAMES
  if (t <= keys[0][0]) return keys[0][1]
  if (t >= keys[keys.length - 1][0]) return keys[keys.length - 1][1]
  for (let i = 1; i < keys.length; i++) {
    const [t1, z1] = keys[i]
    if (t <= t1) {
      const [t0, z0] = keys[i - 1]
      const f = (t - t0) / (t1 - t0)
      const eased = f * f * (3 - 2 * f)
      return z0 + (z1 - z0) * eased
    }
  }
  return keys[keys.length - 1][1]
}

// Sunbeam + hazy glow composition. Emerges around the "lonely speck"
// crescendo (~t=205) and fully forms a few seconds later, so the dot
// is born already wrapped in the iconic Voyager light artifacts.
const SUNBEAM_FADE_START = 198
const SUNBEAM_FADE_END = 218

// Earth scale + crossfade timing keyed to Sagan's "Our planet is a lonely
// speck in the great enveloping cosmic dark" (line 200.78-208.34, with
// the word "speck" at 204.7-205.3). The 3D Earth dissolves into the CSS
// dot exactly as Sagan names Earth "a lonely speck" — symbolism and
// visual transition land together. The 3D model is fully gone before
// the CSS dot starts emerging.
const EARTH_FADE_START = 197
const EARTH_FADE_END = 204
const DOT_FADE_START = 204
const DOT_FADE_END = 208

// Background-stars fade. Times so the starfield is fully BLACK by the
// time Sagan begins "Our planet is a lonely speck..." (~t=200). The
// iconic dot then emerges from a true void rather than a star-strewn sky.
const STARS_FADE_START = 175
const STARS_FADE_END = 200

// Camera direction (unit vector from origin → camera position). Set so
// the camera ends up 90° from the sun-light direction — the visible
// hemisphere is split half-lit / half-dark (the terminator runs through
// the middle of Earth) instead of fully facing the dark side.
const CAM_DIR_X = -0.78
const CAM_DIR_Y =  0.26
const CAM_DIR_Z =  0.57

// (absolute_audio_time, scale_multiplier) — linear interpolation between
// keyframes. Earth lingers visible through the first half of the speech,
// then dramatically shrinks across the final 10s before "lonely speck"
// — the model literally becomes a speck right as Sagan names it one.
const EARTH_SCALE_KEYS = [
  [SEQUENCE_START_TIME, 1.00],
  [150,                 0.95],
  [185,                 0.65],   // visibly receding
  [197,                 0.28],   // shrinking fast
  [202,                 0.06],
  [204,                 0.003],  // sub-pixel + faded — about to become the CSS dot
]

function earthScaleAt(t) {
  const keys = EARTH_SCALE_KEYS
  if (t <= keys[0][0]) return keys[0][1]
  if (t >= keys[keys.length - 1][0]) return keys[keys.length - 1][1]
  for (let i = 1; i < keys.length; i++) {
    const [t1, s1] = keys[i]
    if (t <= t1) {
      const [t0, s0] = keys[i - 1]
      const f = (t - t0) / (t1 - t0)
      return s0 + (s1 - s0) * f
    }
  }
  return keys[keys.length - 1][1]
}

function clamp01(x) { return Math.max(0, Math.min(1, x)) }
function smooth01(x) { const u = clamp01(x); return u * u * (3 - 2 * u) }

// Per-style cadence — leadIn fades the word in, hold keeps it at full
// opacity for a beat past the spoken end, fadeOut dissolves it. Style
// tunes pace only; motion is the same elegant fade for everyone.
const STYLE_CADENCE = {
  body:      { leadIn: 0.45, hold: 0.30, fadeOut: 0.95 },
  emphasis:  { leadIn: 0.75, hold: 0.65, fadeOut: 1.30 },
  crescendo: { leadIn: 0.30, hold: 0.20, fadeOut: 0.80 },
  outro:     { leadIn: 0.70, hold: 0.50, fadeOut: 1.40 },
  intro:     { leadIn: 0.55, hold: 0.40, fadeOut: 1.10 },
}
function cadenceFor(style) { return STYLE_CADENCE[style] || STYLE_CADENCE.body }

export class PaleBlueDotSequence {
  constructor(cosmicScene = null) {
    this.cosmicScene = cosmicScene
    this.transcript = null
    this.duration = 270
    this.audio = null
    this.running = false
    this.raf = 0
    this.typoEl = null
    this.sunbeamsEl = null
    this.hazeEl = null
    this.nodes = []
    this._endHandler = null
    this._pauseHandler = null
    this._savedCameraPos = null
    this._savedCameraTarget = null
    this._earthBaseScale = null
    this._dotEl = null
    this._savedSkyboxPos = null
    this._savedSunPos = null
    // Wall-clock anchor so the camera blend can start immediately when
    // start() is called, even though the audio is paused for the 3s
    // pre-roll. After the audio actually begins playing, the camera
    // hands off to audio-time-driven CAM_KEYFRAMES.
    this._sequenceWallStart = 0
    this._savedStarsBrightness = null
    // Mobile Safari (and a few other mobile browsers) sometimes pause
    // audio mid-playback or freeze audio.currentTime when the tab is
    // backgrounded, the screen sleeps, or another audio source steps in.
    // We anchor to audio.currentTime when it's progressing normally, but
    // fall back to wall-clock if the audio time goes stale, so the
    // typography keeps flowing through the rest of the speech.
    this._audioHasStarted = false
    this._typoTimeAnchor = 0
    this._typoWallAnchor = 0
    this._typoT = 0
  }

  _getEarthGroup() {
    const cs = this.cosmicScene
    if (!cs?.currentGroup || cs.currentBodyId !== 'earth') return null
    return cs.currentGroup
  }

  _setEarthOpacity(group, opacity) {
    group.traverse((c) => {
      if (c.isMesh && c.material) {
        const mats = Array.isArray(c.material) ? c.material : [c.material]
        for (const m of mats) m.opacity = opacity
      }
    })
  }

  async _loadTranscript() {
    if (this.transcript) return
    try {
      const r = await fetch(TRANSCRIPT_URL, { cache: 'no-store' })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      this.transcript = Array.isArray(d) ? d : (Array.isArray(d?.phrases) ? d.phrases : [])
      if (typeof d?.duration === 'number') this.duration = d.duration
    } catch (e) {
      console.error('[pale-blue-dot] transcript load failed', e)
      this.transcript = []
    }
  }

  async start(audio) {
    if (this.running) return
    this.audio = audio
    await this._loadTranscript()
    if (!this.transcript || !this.transcript.length) return

    this._buildOverlay()
    document.body.classList.add('pbd-active')

    // Capture Earth's intrinsic scale and mark its materials transparent so
    // the tick loop can fade the model out as the CSS pixel takes over.
    const earthGroup = this._getEarthGroup()
    if (earthGroup) {
      this._earthBaseScale = earthGroup.scale.x || 1
      earthGroup.traverse((c) => {
        if (c.isMesh && c.material) {
          const mats = Array.isArray(c.material) ? c.material : [c.material]
          for (const m of mats) m.transparent = true
        }
      })
    }

    // The parent's skybox is a radius-900 sphere anchored at origin. As the
    // camera flies past z=100 it gets close to the sphere's near edge and
    // the texture sampling stretches into a dark blob at view center. Save
    // its position so we can pin it to the camera each frame and restore on
    // stop.
    if (this.cosmicScene?.skyboxMesh) {
      this._savedSkyboxPos = this.cosmicScene.skyboxMesh.position.clone()
      const u = this.cosmicScene.skyboxMesh.material?.uniforms?.uBrightness
      if (u) this._savedStarsBrightness = u.value
    }

    // Pivot the visual Sun well off-frame. The original Voyager photograph
    // has the Sun just outside the lens — only its sunbeams enter the
    // shot. Save the position and snap it far to the upper-left so the
    // Sun mesh + lensflare are out of frame at every camera distance,
    // while the directional sunLight (which only uses position as a
    // direction vector) keeps its original cast on Earth.
    if (this.cosmicScene?.distantSunGroup) {
      this._savedSunPos = this.cosmicScene.distantSunGroup.position.clone()
      this.cosmicScene.distantSunGroup.position.set(-650, 220, -180)
    }

    // Save current camera state so we can restore it on stop. Disable
    // OrbitControls so the user can't fight the cinematic path. We do
    // NOT snap to a centered view here — the tick's blend phase smoothly
    // pulls the camera back from whatever intro position it was in,
    // toward a centered framing of Earth, in real time.
    if (this.cosmicScene?.camera && this.cosmicScene?.controls) {
      this._savedCameraPos = this.cosmicScene.camera.position.clone()
      this._savedCameraTarget = this.cosmicScene.controls.target.clone()
      this.cosmicScene.disableControls()
    }

    this.running = true
    this._sequenceWallStart = performance.now()
    this._tick()

    this._endHandler = () => this.stop()
    this._pauseHandler = () => {
      // Don't tear down on a transient pause/seek — only on a real stop.
      if (this.audio.ended) this.stop()
    }
    audio.addEventListener('ended', this._endHandler, { once: true })
    audio.addEventListener('pause', this._pauseHandler)
  }

  stop() {
    if (!this.running) return
    this.running = false
    cancelAnimationFrame(this.raf)

    if (this.audio) {
      if (this._endHandler) this.audio.removeEventListener('ended', this._endHandler)
      if (this._pauseHandler) this.audio.removeEventListener('pause', this._pauseHandler)
    }

    document.body.classList.remove('pbd-active')

    // Restore Earth to full size + opacity + visibility, clear the dot's
    // inline override so its CSS state takes over again.
    const earthGroup = this._getEarthGroup()
    if (earthGroup && this._earthBaseScale != null) {
      earthGroup.scale.setScalar(this._earthBaseScale)
      earthGroup.visible = true
      this._setEarthOpacity(earthGroup, 1)
    }
    this._earthBaseScale = null
    // Pull the sequence-only overlays out of the DOM so nothing lingers
    // on screen after the user hits Return.
    if (this._dotEl) { this._dotEl.remove(); this._dotEl = null }
    if (this.sunbeamsEl) { this.sunbeamsEl.remove(); this.sunbeamsEl = null }
    if (this.hazeEl) { this.hazeEl.remove(); this.hazeEl = null }
    if (this.typoEl) { this.typoEl.remove(); this.typoEl = null }
    this.nodes = []
    if (this.cosmicScene?.skyboxMesh && this._savedSkyboxPos) {
      this.cosmicScene.skyboxMesh.position.copy(this._savedSkyboxPos)
    }
    this._savedSkyboxPos = null
    if (this.cosmicScene?.skyboxMesh?.material?.uniforms?.uBrightness && this._savedStarsBrightness != null) {
      this.cosmicScene.skyboxMesh.material.uniforms.uBrightness.value = this._savedStarsBrightness
    }
    this._savedStarsBrightness = null
    if (this.cosmicScene?.distantSunGroup && this._savedSunPos) {
      this.cosmicScene.distantSunGroup.position.copy(this._savedSunPos)
    }
    this._savedSunPos = null

    // Restore camera to the landing view, then re-enable OrbitControls.
    if (this.cosmicScene?.camera && this._savedCameraPos && this._savedCameraTarget) {
      this.cosmicScene.animateCamera(
        this._savedCameraPos,
        this._savedCameraTarget,
        2200
      ).then(() => {
        if (this.cosmicScene?.enableControls) this.cosmicScene.enableControls()
      })
    }

  }

  _buildOverlay() {
    let container = document.getElementById('pbd-typography')
    if (!container) {
      container = document.createElement('div')
      container.id = 'pbd-typography'
      container.className = 'pbd-typography'
      document.body.appendChild(container)
    }
    this.typoEl = container

    // Dedicated pale-blue-dot pixel for the Sagan sequence. The .pale-blue-dot
    // in the landing layout lives inside #intro-phase, which gets opacity:0
    // during pbd-active and would drag this with it. Own element, own opacity.
    let dot = document.getElementById('pbd-dot')
    if (!dot) {
      dot = document.createElement('div')
      dot.id = 'pbd-dot'
      dot.className = 'pbd-dot'
      dot.setAttribute('aria-hidden', 'true')
      document.body.appendChild(dot)
    }
    dot.style.opacity = '0'
    this._dotEl = dot

    // Hazy atmospheric glow — a soft radial gradient centered behind the
    // dot, suggesting the lens-flare/sunlight diffusion in the original
    // Voyager photograph. JS drives its opacity in lockstep with the
    // sunbeams so the iconic image forms together.
    let haze = document.getElementById('pbd-haze')
    if (!haze) {
      haze = document.createElement('div')
      haze.id = 'pbd-haze'
      haze.className = 'pbd-haze'
      haze.setAttribute('aria-hidden', 'true')
      document.body.appendChild(haze)
    }
    this.hazeEl = haze

    let sunbeams = document.getElementById('pbd-sunbeams')
    if (!sunbeams) {
      sunbeams = document.createElement('div')
      sunbeams.id = 'pbd-sunbeams'
      sunbeams.className = 'pbd-sunbeams'
      sunbeams.setAttribute('aria-hidden', 'true')
      for (let i = 1; i <= 3; i++) {
        const beam = document.createElement('div')
        beam.className = `pbd-beam pbd-beam-${i}`
        sunbeams.appendChild(beam)
      }
      // Insert before the typography so typography sits on top.
      document.body.appendChild(sunbeams)
    }
    this.sunbeamsEl = sunbeams

    while (container.firstChild) container.removeChild(container.firstChild)

    this.nodes = this.transcript.map((entry, i) => {
      const el = document.createElement('p')
      el.className = 'pbd-phrase'
      const style = entry.style || 'body'
      el.dataset.style = style
      const cadence = cadenceFor(style)

      const wordSpans = []
      const words = Array.isArray(entry.words) ? entry.words : []
      let validIdx = 0
      words.forEach((w) => {
        const raw = (w.word ?? '').trim()
        if (!raw) return
        if (validIdx > 0) el.appendChild(document.createTextNode(' '))
        const span = document.createElement('span')
        span.className = 'pbd-word'
        // Two CSS variables drive everything for this word:
        //   --pbd-op   opacity 0→1 (entry ramp, hold at 1, exit ramp)
        //   --pbd-exit 0→1 (only during the dissolve — adds a subtle
        //                   upward drift + slight blur on the way out)
        // Both updated every frame from _updateWords() as a pure function
        // of audio.currentTime. CSS reads them; no transitions in play.
        span.style.setProperty('--pbd-op', '0')
        span.style.setProperty('--pbd-exit', '0')
        span.appendChild(document.createTextNode(raw))
        el.appendChild(span)
        wordSpans.push({
          span,
          start: w.start,
          end: w.end,
          leadIn: cadence.leadIn,
          hold: cadence.hold,
          fadeOut: cadence.fadeOut,
          // Cached last values so we skip redundant style writes.
          lastOp: -1,
          lastExit: -1,
        })
        validIdx += 1
      })

      container.appendChild(el)
      return { id: i, el, entry, words: wordSpans, cadence }
    })

    // Clip phrase-boundary word lifecycles. Within-phrase consecutive
    // words are NOT clipped (different X positions). The next phrase's
    // leadIn is SACRED — it's the reader's first impression of a new
    // sentence, so we keep it at full duration. Instead we squash the
    // previous phrase's hold + fadeOut so its life ends before the next
    // phrase's first word's lifeStart. The two phrases may briefly
    // co-exist (last word of prev fading at right side of line, first
    // word of next rising at left side), but each behaves with full
    // cadence on its own end.
    const PHRASE_GAP = 0.05
    for (let i = 0; i < this.nodes.length - 1; i++) {
      const phrase = this.nodes[i]
      const nextPhrase = this.nodes[i + 1]
      const lastWord = phrase.words[phrase.words.length - 1]
      const firstNextWord = nextPhrase.words[0]
      if (!lastWord || !firstNextWord) continue

      const nextLifeStart = firstNextWord.start - firstNextWord.leadIn
      const limit = nextLifeStart - PHRASE_GAP

      // Squash prev's hold first (least essential), then trim its fadeOut
      // so prev's whole life ends by `limit`. Floors keep the prev word
      // from popping (instead it dissolves quickly, which is acceptable).
      if (lastWord.end + lastWord.hold + lastWord.fadeOut > limit) {
        const tailRoom = limit - lastWord.end
        if (tailRoom <= 0) {
          lastWord.hold = 0
          lastWord.fadeOut = 0.18
        } else {
          // Reserve fadeOut first (visible dissolve), give hold the rest.
          const minFadeOut = Math.min(0.18, tailRoom * 0.6)
          lastWord.fadeOut = Math.min(lastWord.fadeOut, Math.max(minFadeOut, tailRoom * 0.6))
          lastWord.hold = Math.max(0, tailRoom - lastWord.fadeOut)
        }
      }

      // All OTHER words in prev phrase get clipped to end before `limit`.
      for (const w of phrase.words) {
        if (w === lastWord) continue
        const apexEnd = w.end + w.hold
        const maxFadeOut = limit - apexEnd
        if (w.fadeOut > maxFadeOut) {
          w.fadeOut = Math.max(0.18, maxFadeOut)
        }
      }
    }
  }

  _tick() {
    if (!this.running) return
    this.raf = requestAnimationFrame(() => this._tick())
    if (!this.audio) return

    const t = this.audio.currentTime
    const audioPlaying = !this.audio.paused
    // Once audio first plays, lock in a typography clock that's robust to
    // audio.currentTime stalling (background tabs, mobile RAF throttling,
    // audio element being suspended by the browser). We re-anchor whenever
    // audio.currentTime is fresh and progressing, but if it stops moving
    // we extrapolate via wall-clock so the words keep flowing.
    if (!this._audioHasStarted) {
      if (audioPlaying && t > 0.001) {
        this._audioHasStarted = true
        this._typoTimeAnchor = t
        this._typoWallAnchor = performance.now()
        this._typoT = t
      }
    }
    if (this._audioHasStarted) {
      const wallEstimate = this._typoTimeAnchor + (performance.now() - this._typoWallAnchor) / 1000
      // If audio.currentTime is moving, trust it and re-anchor.
      if (audioPlaying && t > this._typoT - 0.05) {
        this._typoT = Math.max(t, this._typoT)
        this._typoTimeAnchor = t
        this._typoWallAnchor = performance.now()
      } else {
        // Audio stalled or paused — keep the typography flowing on
        // wall-clock so the rest of the speech still renders even if the
        // audio element hangs (mobile Chrome / iOS Safari can do this).
        this._typoT = Math.max(this._typoT, wallEstimate)
      }
      this._updateWords(this._typoT)
    }

    // Camera path:
    //   • Initial blend (wall-clock 0 → SETTLE_S): orbit-arc from the
    //     user's saved intro camera around Earth to CAM_DIR×5.5 (a half-
    //     Earth view ~90° from the sun-light direction). The arc is a
    //     slerp of unit direction + lerp of radius, clamped to a safe
    //     minimum so the camera never passes through Earth.
    //   • After SETTLE_S: camera position = CAM_DIR × cameraZAt(t),
    //     looking back at origin. The CAM_KEYFRAMES first entry is at
    //     z=5.5 so the handoff is seamless.
    if (this.cosmicScene?.camera) {
      const cam = this.cosmicScene.camera
      const ctrls = this.cosmicScene.controls
      // Long, contemplative settling — matches the original audio-time
      // blend duration (24s of audio = 24s wall-clock since audio plays
      // at 1x). The wall-clock anchor lets the blend start the moment
      // the user clicks, while the 3s VO pre-roll waits separately.
      const SETTLE_S = 24.0
      const wallElapsed = (performance.now() - this._sequenceWallStart) / 1000
      const blendProgress = clamp01(wallElapsed / SETTLE_S)
      const destDist = 5.5
      if (blendProgress < 1.0 && this._savedCameraPos && this._savedCameraTarget) {
        const eased = blendProgress * blendProgress * (3 - 2 * blendProgress)
        const sp = this._savedCameraPos
        const st = this._savedCameraTarget
        // Predictable horizontal orbit around the Y axis. Decompose both
        // the saved position and the destination into (yaw, xzRadius, y),
        // interpolate each separately taking the shorter yaw direction,
        // then re-compose. At eased=0 this reproduces the saved camera
        // position EXACTLY — the blend visibly starts from where the user
        // had the camera at click time. xzRadius lerps freely; if the
        // user's saved position drops below a safety threshold, a small
        // bow-out term gently pushes the mid-arc outward without ever
        // jumping the start frame.
        const tgtX = CAM_DIR_X * destDist
        const tgtY = CAM_DIR_Y * destDist
        const tgtZ = CAM_DIR_Z * destDist
        const savedXZ = Math.sqrt(sp.x * sp.x + sp.z * sp.z)
        const tgtXZ   = Math.sqrt(tgtX * tgtX + tgtZ * tgtZ)
        const savedYaw = Math.atan2(sp.x, sp.z)
        const tgtYaw   = Math.atan2(tgtX, tgtZ)
        let yawDelta = tgtYaw - savedYaw
        if (yawDelta >  Math.PI) yawDelta -= 2 * Math.PI
        if (yawDelta < -Math.PI) yawDelta += 2 * Math.PI
        const yaw = savedYaw + yawDelta * eased
        const baseXZ = savedXZ * (1 - eased) + tgtXZ * eased
        // Bow-out: peaks at mid-arc, zero at both endpoints. Only kicks in
        // if both endpoints are inside the safe ring; otherwise no effect.
        const SAFE_XZ = 3.0
        const minEnd = Math.min(savedXZ, tgtXZ)
        const bow = minEnd < SAFE_XZ ? (SAFE_XZ - minEnd) * 4 * eased * (1 - eased) : 0
        const xzRadius = baseXZ + bow
        const y = sp.y * (1 - eased) + tgtY * eased
        cam.position.x = Math.sin(yaw) * xzRadius
        cam.position.y = y
        cam.position.z = Math.cos(yaw) * xzRadius
        if (ctrls) {
          ctrls.target.x = st.x * (1 - eased)
          ctrls.target.y = st.y * (1 - eased)
          ctrls.target.z = st.z * (1 - eased)
          cam.lookAt(ctrls.target.x, ctrls.target.y, ctrls.target.z)
          ctrls.enabled = false
        } else {
          cam.lookAt(0, 0, 0)
        }
      } else {
        const dist = cameraZAt(t)
        cam.position.set(CAM_DIR_X * dist, CAM_DIR_Y * dist, CAM_DIR_Z * dist)
        cam.lookAt(0, 0, 0)
        if (ctrls) {
          ctrls.target.set(0, 0, 0)
          // Parent's drift-detection or other handlers can re-enable controls
          // mid-sequence; force them off every frame while we're driving.
          ctrls.enabled = false
        }
      }
      // Pin the skybox to the camera so it always feels infinitely far
      // (otherwise, with the sphere anchored at origin, the camera ends up
      // close to the sphere's near edge and the texture warps to a dark blob).
      if (this.cosmicScene.skyboxMesh) {
        this.cosmicScene.skyboxMesh.position.copy(cam.position)
      }
    }

    // Background-stars fade. The skybox shader has a uBrightness uniform
    // that multiplies its texture sample; ramping it from 1.0 → 0 over
    // the final stretch dims the stars to near-black so the iconic
    // Voyager image (dot + sunbeams + haze) ends in a pure void.
    if (this.cosmicScene?.skyboxMesh?.material?.uniforms?.uBrightness) {
      const span = STARS_FADE_END - STARS_FADE_START
      const fadeP = clamp01((t - STARS_FADE_START) / span)
      const baseBright = this._savedStarsBrightness ?? 1.0
      this.cosmicScene.skyboxMesh.material.uniforms.uBrightness.value = baseBright * (1 - fadeP)
    }

    // Sunbeam + hazy glow composition fades in together so the iconic
    // Voyager photograph is fully reimagined by "suspended in a sunbeam"
    // (~143s).
    {
      const span = SUNBEAM_FADE_END - SUNBEAM_FADE_START
      const beamP = clamp01((t - SUNBEAM_FADE_START) / span)
      if (this.sunbeamsEl) this.sunbeamsEl.style.opacity = String(beamP)
      if (this.hazeEl) this.hazeEl.style.opacity = String(beamP)
    }

    // Earth: shrink the model on the artistic curve, then fade it out
    // entirely. Hide once fully faded — leaving a transparent mesh in the
    // scene can register through bloom/tone-mapping as a faint halo at
    // center. The CSS dot only emerges AFTER the model is gone.
    const earthGroup = this._getEarthGroup()
    if (earthGroup && this._earthBaseScale != null) {
      earthGroup.scale.setScalar(this._earthBaseScale * earthScaleAt(t))
      const earthFadeSpan = Math.max(0.001, EARTH_FADE_END - EARTH_FADE_START)
      const earthOpacity = 1 - clamp01((t - EARTH_FADE_START) / earthFadeSpan)
      this._setEarthOpacity(earthGroup, earthOpacity)
      earthGroup.visible = earthOpacity > 0.001
    }

    if (this._dotEl) {
      const dotSpan = Math.max(0.001, DOT_FADE_END - DOT_FADE_START)
      this._dotEl.style.opacity = String(clamp01((t - DOT_FADE_START) / dotSpan))
    }
  }

  // Per-word lifecycle — pure function of audio.currentTime, no CSS
  // transitions in play. Three phases:
  //   1. Fade-in:  opacity 0 → 1 across leadIn (smoothstep)
  //   2. Hold:     opacity 1 from word.start through word.end + hold
  //   3. Dissolve: opacity 1 → 0 across fadeOut, with a subtle upward
  //                drift + soft blur (driven by --pbd-exit)
  // Each word lives its full lifecycle independently. There's no phrase-
  // level gate — the previous design clipped the phrase wrapper at the
  // next phrase's lead-in, which cut the last word's dissolve short.
  _updateWords(t) {
    for (const n of this.nodes) {
      for (const w of n.words) {
        const wStart = w.start
        const wEnd = w.end
        const apexEnd = wEnd + w.hold
        const lifeStart = wStart - w.leadIn
        const lifeEnd = apexEnd + w.fadeOut

        // Cheap early-out for words far from the cursor — they're at
        // op=0/exit=0 and have already been written there once.
        if (t < lifeStart - 0.5 || t > lifeEnd + 0.5) {
          if (w.lastOp !== 0) {
            w.span.style.setProperty('--pbd-op', '0')
            w.lastOp = 0
          }
          if (w.lastExit !== 0) {
            w.span.style.setProperty('--pbd-exit', '0')
            w.lastExit = 0
          }
          continue
        }

        let op = 0
        let exit = 0
        if (t <= lifeStart) {
          op = 0
        } else if (t < wStart) {
          op = smooth01((t - lifeStart) / w.leadIn)
        } else if (t <= apexEnd) {
          op = 1
        } else if (t < lifeEnd) {
          const u = (t - apexEnd) / w.fadeOut
          const e = smooth01(u)
          op = 1 - e
          exit = e
        } else {
          op = 0
          exit = 1
        }

        if (Math.abs(op - w.lastOp) > 0.005 || (op === 1 && w.lastOp !== 1) || (op === 0 && w.lastOp !== 0)) {
          w.span.style.setProperty('--pbd-op', op.toFixed(3))
          w.lastOp = op
        }
        if (Math.abs(exit - w.lastExit) > 0.005 || (exit === 1 && w.lastExit !== 1) || (exit === 0 && w.lastExit !== 0)) {
          w.span.style.setProperty('--pbd-exit', exit.toFixed(3))
          w.lastExit = exit
        }
      }
    }
  }
}
